import get from 'lodash/get';
import map from 'lodash/map';
import sum from 'lodash/sum';
import min from 'lodash/min';
import find from 'lodash/find';
import size from 'lodash/size';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import slice from 'lodash/slice';
import forEach from 'lodash/forEach';
import reverse from 'lodash/reverse';
import zipObject from 'lodash/zipObject';

import {
    ServerMatchData
} from '../../data_types/interfaces';
import { resolveIconSelection } from '../player_icons';

import {
    MatchSummaryData,
    HeadToHeadRecords,
    NormalizedMatchData,
    NormalizedMatchPlayerData
} from '../../data_types/types';

import { STATUS_ROUND_COMPLETE } from '../const';


const MIN_ROUNDS_PLAYED = 3;


/**
 * normalizeMatchData converts the match data we receive from the server into a
 * condensed version that includes the winner(s), and player data about how
 * they performed on the layout.
 */
export const normalizeMatchData = (matchData: ServerMatchData[]): NormalizedMatchData[]=> {
    const results: NormalizedMatchData[] = [];

    const completedMatches = filter(matchData, (match)=> {
        return match.s === 'complete';
    });

    forEach(completedMatches, (match)=> {
        const matchResult: NormalizedMatchData = {
            timestamp: match.timestamp,
            cardSize: size(match.players),
            winners: [],
            layout: match.layout,
            playerData: []
        };

        forEach(match.players, (player, pID)=> {
            const scores: number[] = get(match, `scores.${pID}.s`, []);

            const playerDataResult = {
                pID: pID.toString(),
                status: player.status,
                name: player.name,
                ...resolveIconSelection(player),
                totalScore: sum(scores),
                scoreCard: zipObject(match.layout, scores)
            };

            matchResult['playerData'].push(playerDataResult);
        })

        const winningScore = getWinningScoreFromPlayerData(matchResult.playerData) || 0;

        matchResult['winners'] = map(filter(matchResult.playerData, ['totalScore', winningScore]), 'pID');

        results.push(matchResult);
    });

    return results;
};


/**
 * Head to head outcome from my player's point of view. A retirement loses to
 * a finished round; otherwise the lower total score wins.
 */
export const getHeadToHeadResult = (me: NormalizedMatchPlayerData, opp: NormalizedMatchPlayerData): 'w' | 't' | 'l' => {
    const didMyPlayerRetire = me.status !== STATUS_ROUND_COMPLETE;
    const didOpponentRetire = opp.status !== STATUS_ROUND_COMPLETE;

    if (didMyPlayerRetire && didOpponentRetire) return 't';
    if (didMyPlayerRetire) return 'l';
    if (didOpponentRetire) return 'w';
    if (opp.totalScore > me.totalScore) return 'w';
    if (opp.totalScore < me.totalScore) return 'l';
    return 't';
};


export const generateHeadToHeadRecords = (myPID: string, matchData: NormalizedMatchData[]): HeadToHeadRecords => {
    const records: HeadToHeadRecords = {};

    forEach(matchData, (match)=> {
        const myPlayer = find(match.playerData, ['pID', myPID]);

        forEach(match.playerData, (playerData)=> {
            if (playerData.pID !== myPID) {
                if (!records.hasOwnProperty(playerData.pID)) {
                    records[playerData.pID] = {
                        name: playerData.name,
                        pID: playerData.pID,
                        strokeDiff: 0,
                        timesPlayed: 0,
                        w: 0,
                        t: 0,
                        l: 0
                    };
                }

                const opp = records[playerData.pID];
                const result = getHeadToHeadResult(myPlayer!, playerData);

                records[playerData.pID] = {
                    ...opp,
                    timesPlayed: opp.timesPlayed + 1,
                    strokeDiff: opp.strokeDiff + calculateStrokeDiff(myPlayer, playerData),
                    [result]: opp[result] + 1,
                };
            }
        })
    })

    return records;
};


export const generatedMatchSummaryData = (records: HeadToHeadRecords): MatchSummaryData=> {
    const numRecords = 5;

    return {
        best_records: getBestRecords(records, numRecords),
        worst_records: getWorstRecords(records, numRecords),
        most_played: getMostPlayedOpponents(records, numRecords)
    };
};


const getWinningScoreFromPlayerData = (playerData: NormalizedMatchPlayerData[]): number | string | undefined=> {
    return min(map(playerData, (data)=> {
        // make sure we exclude players who didn't finish the match
        if (data.status === STATUS_ROUND_COMPLETE) {
            return data.totalScore;
        }
        return 100000;
    }));
};


export const calculateStrokeDiff = (me: NormalizedMatchPlayerData | undefined, opp: NormalizedMatchPlayerData): number=> {
    if (!me) {
        return 0;
    }

    const didMyPlayerRetire = me!.status !== STATUS_ROUND_COMPLETE;
    const didOpponentRetire = opp.status !== STATUS_ROUND_COMPLETE;

    // both players didn't finish the match
    if (didMyPlayerRetire && didOpponentRetire) {
        return 0;
    }

    // my player didn't finish the match, they did
    if (didMyPlayerRetire && !didOpponentRetire) {
        return me!.totalScore - opp.totalScore;
    }

    // their player didn't finish the match, mine did
    if (!didMyPlayerRetire && didOpponentRetire) {
        return me!.totalScore - opp.totalScore;
    }

    // my player won the match
    if (me!.totalScore < opp.totalScore) {
        return opp.totalScore - me!.totalScore;
    }

    // my player lost the match
    if (me!.totalScore > opp.totalScore) {
        return opp.totalScore - me!.totalScore;
    }

    // it was a draw or some other exclusion happened
    return 0;

};


export const getBestRecords = (records: HeadToHeadRecords, numRecords: number)=> {
    const filteredRecords = filter(records, record=> record.timesPlayed >= MIN_ROUNDS_PLAYED);
    const sortedRecords = reverse(sortBy(filteredRecords, (record)=> {
        return record.w / record.timesPlayed;
    }));

    return slice(sortedRecords, 0, numRecords);
};


export const getWorstRecords = (records: HeadToHeadRecords, numRecords: number)=> {
    const filteredRecords = filter(records, record=> record.timesPlayed >= MIN_ROUNDS_PLAYED);
    const sortedRecords = reverse(sortBy(filteredRecords, (record)=> {
        return record.l / record.timesPlayed;
    }));

    return slice(sortedRecords, 0, numRecords);
};


export const getMostPlayedOpponents = (records: HeadToHeadRecords, numRecords: number)=>
    slice(reverse(sortBy(records, 'timesPlayed')), 0, numRecords);
